import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, BookOpen, MapPin } from 'lucide-react';
import axios from 'axios';
import WeatherChip from './WeatherChip';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_07e05c69-0586-4274-8381-f4bbddcb034e/artifacts/bdl556y9_DJI_0001.JPG';
const DEFAULT_CAPTION = 'Paisley Abbey & the High Street, photographed for paisley.org.uk';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Full-bleed hero: the Abbey photograph spans edge to edge, with the headline
 * and calls to action laid over the left side behind a soft gradient scrim.
 */
const Hero = () => {
  const [heroUrl, setHeroUrl] = useState(HERO_IMAGE);
  const [heroCaption, setHeroCaption] = useState(DEFAULT_CAPTION);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/hero-image`)
      .then((r) => {
        if (!alive) return;
        if (r.data?.url) setHeroUrl(r.data.url);
        if (r.data?.caption) setHeroCaption(r.data.caption);
      })
      .catch(() => { /* keep default */ });
    return () => { alive = false; };
  }, []);

  return (
    <section className="relative w-full overflow-hidden" data-testid="liquid-glass-hero">
      {/* Photograph — full width, behind everything */}
      <div className="absolute inset-0">
        <img
          src={heroUrl}
          alt="Paisley Abbey and the town centre from above"
          className="w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          data-testid="hero-image"
        />
        {/* Scrim: heavier on the left so the type stays readable, clear on the right
            so the Abbey itself is never buried. */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/55 to-slate-950/15 sm:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-slate-950/35" />
      </div>

      {/* Type overlay */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-14 md:pt-44 md:pb-24 min-h-[560px] md:min-h-[680px] flex flex-col justify-center">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2.5 rounded-full bg-white/10 px-4 py-1.5 ring-1 ring-white/20 backdrop-blur-md" data-testid="hero-pill">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inset-0 rounded-full bg-sky-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-400" />
            </span>
            <span className="text-xs font-medium text-white/90 tracking-wide">Live · paisley.org.uk · since 2007</span>
          </div>

          {/* Styled as the visual headline but not an <h1>: the homepage's
              single H1 is the descriptive one in HomePage.jsx. Two H1s on one
              page is what the SEO brief flagged. */}
          <div
            className="mt-7 text-[clamp(3.25rem,9vw,7rem)] font-bold leading-[0.92] tracking-[-0.045em] text-white drop-shadow-[0_2px_24px_rgba(2,6,23,0.5)]"
            data-testid="hero-title"
          >
            Paisley
          </div>
          <p className="mt-3 text-[clamp(1.4rem,3.4vw,2.35rem)] font-semibold tracking-[-0.02em] text-white/95 leading-tight">
            Our town.{' '}
            <span className="font-serif-accent italic font-light text-sky-200">Our stories.</span>
          </p>
          <p className="mt-6 max-w-md font-medium text-white/80 text-base md:text-lg leading-relaxed">
            News, events, history and community for the people of Paisley and Renfrewshire.
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-9">
            <Link
              to="/news"
              className="group inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-sky-50 px-7 py-3.5 rounded-full text-sm font-semibold tracking-tight transition-all hover:scale-[1.02]"
              data-testid="hero-cta-news"
            >
              Read the latest
              <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/events"
              className="group inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-7 py-3.5 rounded-full text-sm font-semibold tracking-tight ring-1 ring-white/25 backdrop-blur-md transition-colors duration-200"
            >
              <Calendar size={15} />
              Browse the calendar
            </Link>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-semibold text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <BookOpen size={13} className="text-sky-300" />10,500+ articles
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={13} className="text-sky-300" />Paisley &amp; Renfrewshire
            </span>
          </div>
        </div>
      </div>

      {/* Weather chip and photo credit sit on the photograph */}
      <div className="absolute top-24 right-5 md:top-28 md:right-8 z-10 hidden sm:block">
        <WeatherChip />
      </div>
      <div className="absolute bottom-4 right-5 md:bottom-6 md:right-8 z-10 hidden sm:flex" data-testid="hero-photo-card">
        <div className="flex items-center gap-2 rounded-2xl bg-slate-950/45 px-4 py-2.5 text-[11px] md:text-xs font-medium text-white/90 ring-1 ring-white/15 backdrop-blur-md max-w-[min(70vw,26rem)]">
          <MapPin size={13} className="text-sky-300 shrink-0" />
          <span className="truncate">{heroCaption}</span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
